import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { Task, Project } from '@/types/product-manager';
import TaskBoard from '@/components/product-manager/TaskBoard';
import TaskStatusTabs from '@/components/product-manager/TaskStatusTabs';
import CreateTaskDialog from '@/components/product-manager/CreateTaskDialog';
import { getAllUserAppAccess, AVAILABLE_APPS } from '@/services/appAccess';

// Tasks component
const Tasks: React.FC = () => {
  const renderCountRef = useRef(0);
  const componentIdRef = useRef(Math.random().toString(36).substr(2, 9));
  renderCountRef.current += 1;
  console.log(`[Tasks] Component ${componentIdRef.current} rendering... (render #${renderCountRef.current})`);
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'todo' | 'in-progress' | 'completed'>('todo');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [availableAssignees, setAvailableAssignees] = useState<string[]>([]);
  
  // Use ref to prevent multiple simultaneous data loads
  const isLoadingRef = useRef(false);

  const loadData = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      console.log('[Tasks] loadData already in progress, skipping...');
      return;
    }
    
    isLoadingRef.current = true;
    console.log('[Tasks] loadData called');
    try {
      setLoading(true);
      
      // Import the functions inside the callback to avoid dependency issues
      const { getTasks: getTasksService, getProjects: getProjectsService } = await import('@/services/productManagerService');
      
      const [tasksData, projectsData] = await Promise.all([
        getTasksService(),
        getProjectsService()
      ]);
      
      console.log('[Tasks] Data loaded, setting state...');
      setTasks(tasksData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading tasks data:', error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false; // Reset loading flag
    }
  }, []); // Empty dependency array to make this function stable

  // Load Product Manager users for assignee dropdown
  const loadProductManagerUsers = useCallback(async () => {
    try {
      const allUserAccess = await getAllUserAppAccess();
      const productManagerUsers = allUserAccess
        .filter(userAccess => userAccess.allowedApps.includes(AVAILABLE_APPS.ProductManager))
        .map(userAccess => userAccess.email);
      setAvailableAssignees(productManagerUsers);
    } catch (error) {
      console.error('Error loading Product Manager users:', error);
      setAvailableAssignees([]);
    }
  }, []); // Empty dependency array since this function doesn't depend on other state

  useEffect(() => {
    const componentId = componentIdRef.current;
    console.log(`[Tasks] Component ${componentId} MOUNTED`);
    loadData();
    loadProductManagerUsers();
    
    return () => {
      console.log(`[Tasks] Component ${componentId} UNMOUNTING`);
    };
  }, [loadData, loadProductManagerUsers]); // Include dependencies to fix ESLint warning

  // Memoize filtered tasks to prevent unnecessary recalculations
  const filteredTasks = React.useMemo(() => {
    console.log('[Tasks] Calculating filtered tasks, tasks.length:', tasks.length);
    return tasks.filter(task => {
      const taskTitle = task.title || '';
      const taskDescription = task.description || '';
      const matchesSearch = taskTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           taskDescription.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesProject = selectedProject === 'all' || task.projectId === selectedProject;
      const matchesPriority = selectedPriority === 'all' || task.priority === selectedPriority;
      const matchesAssignee = selectedAssignee === 'all' || task.assignedTo === selectedAssignee;
      
      return matchesSearch && matchesProject && matchesPriority && matchesAssignee;
    });
  }, [tasks, searchTerm, selectedProject, selectedPriority, selectedAssignee]);

  const handleTaskCreated = () => {
    loadData();
    setShowCreateDialog(false);
  };

  const handleTaskUpdated = useCallback(() => {
    console.log(`[Tasks] handleTaskUpdated called (render #${renderCountRef.current})`);
    loadData();
  }, [loadData]); // Include loadData as dependency since it's now stable

  const handleTabChange = (tab: 'todo' | 'in-progress' | 'completed') => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tasks</h1>
            <p className="text-gray-600 dark:text-gray-400">Loading tasks...</p>
          </div>
        </div>
        
        {/* Loading skeleton for tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center py-4 px-1">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded mr-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
              </div>
            ))}
          </nav>
        </div>
        
        {/* Loading skeleton for task grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 animate-pulse">
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                <div className="h-20 bg-gray-200 dark:bg-gray-600 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tasks</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage tasks across all projects
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedPriority} onValueChange={setSelectedPriority}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {availableAssignees.map(assignee => (
                <SelectItem key={assignee} value={assignee}>
                  {assignee}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Task Status Tabs */}
      <TaskStatusTabs
        tasks={filteredTasks}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Task Board */}
      <TaskBoard
        tasks={filteredTasks}
        projects={projects}
        onTaskUpdate={handleTaskUpdated}
        activeTab={activeTab}
      />

      {/* Create Task Dialog */}
      <CreateTaskDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onTaskCreated={handleTaskCreated}
        projects={projects}
      />
    </div>
  );
};

export default Tasks;